import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props { children: React.ReactNode; routeName?: string; }
interface State { hasError: boolean; error: Error | null; }

export class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[RouteErrorBoundary] Error in ${this.props.routeName ?? 'route'}:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Something went wrong{this.props.routeName ? ` in ${this.props.routeName}` : ''}
          </h2>
          <p className="text-sm text-slate-500 max-w-md">
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700"
            >
              <RefreshCw className="w-4 h-4" /> Reload
            </button>
            <a
              href="/dashboard"
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Home className="w-4 h-4" /> Dashboard
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
